import React, { Component } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { managerCheckOut } from '../../../services/timeTrackingService';
import { convertFromUTC } from '../../../util/convert_timezone';

/**
 * ManagerCheckOutModal - Confirmation modal for managers to check out a technician
 *
 * Props:
 * - show: Boolean - whether modal is visible
 * - onHide: Function - called when modal is closed
 * - onSuccess: Function - called after successful check-out
 * - technician: Object - { User_id, user_name, check_in_at } of the technician
 * - locationId: Number - the location ID
 * - actorUserId: Number - the manager's user ID performing the action
 */
class ManagerCheckOutModal extends Component {
  state = {
    note: '',
    loading: false,
    error: null
  };

  componentDidUpdate(prevProps) {
    // Reset state when modal opens
    if (this.props.show && !prevProps.show) {
      this.setState({
        note: '',
        loading: false,
        error: null
      });
    }
  }

  handleNoteChange = (e) => {
    this.setState({ note: e.target.value });
  };

  handleSubmit = async () => {
    const { technician, locationId, onSuccess, onHide, actorUserId } = this.props;
    const { note } = this.state;

    try {
      this.setState({ loading: true, error: null });

      const result = await managerCheckOut(technician.User_id, locationId, '', note, actorUserId);

      // Success - close modal and notify parent
      onHide();
      if (onSuccess) {
        onSuccess(technician, result.duration_minutes);
      }
    } catch (err) {
      console.error('Manager check-out error:', err);
      const errorMsg = err.response?.data?.message || 'Failed to check out technician. Please try again.';
      this.setState({ error: errorMsg, loading: false });
    }
  };

  formatCheckInTime = () => {
    const { technician } = this.props;
    if (!technician?.check_in_at) return 'Unknown';
    return convertFromUTC(technician.check_in_at).format('h:mm A');
  };

  getDuration = () => {
    const { technician } = this.props;
    if (!technician?.check_in_at) return '';
    return convertFromUTC(technician.check_in_at).fromNow(true);
  };

  render() {
    const { show, onHide, technician } = this.props;
    const { note, loading, error } = this.state;

    const technicianName = technician?.user_name || 'Technician';

    return (
      <Modal show={show} onHide={onHide} centered backdrop={loading ? 'static' : true}>
        <Modal.Header closeButton={!loading}>
          <Modal.Title>
            <i className="fa-sharp fa-regular fa-sign-out text-warning mr-2"></i>
            Check Out {technicianName}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Confirmation Message */}
          <div className="alert alert-info">
            <p className="mb-0">
              Are you sure you want to check out <strong>{technicianName}</strong>?
            </p>
            <p className="mb-0 mt-2 text-muted small">
              <i className="fa-sharp fa-regular fa-clock mr-1"></i>
              Checked in at {this.formatCheckInTime()} ({this.getDuration()})
            </p>
          </div>

          {/* Optional Note Field */}
          <div className="form-group mb-0">
            <label>Note (optional)</label>
            <textarea
              className="form-control"
              rows={2}
              value={note}
              onChange={this.handleNoteChange}
              placeholder="Add any notes about this check-out..."
              disabled={loading}
            />
          </div>

          {/* Error Display */}
          {error && (
            <div className="alert alert-danger mt-3 mb-0">
              <i className="fa-sharp fa-regular fa-exclamation-circle mr-2"></i>
              {error}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={this.handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2"></span>
                Checking Out...
              </>
            ) : (
              <>
                <i className="fa-sharp fa-regular fa-sign-out mr-2"></i>
                Check Out
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default ManagerCheckOutModal;
